
import type { StreamEvent, ChatMessage } from "./types";
import { localApiPort } from "../../lib/localApi";

type EventHandler = (e: StreamEvent) => void;
type ChatHandler = (c: ChatMessage) => void;
type ConnectionHandler = (active: boolean) => void;

const CHAT_BUFFER_LIMIT = 200;
const EVENT_SEEN_LIMIT = 1_000;
const RECONNECT_DELAY_MS = 5_000;

class EventBusService {
  private eventSse: EventSource | null = null;
  private chatSse: EventSource | null = null;
  private eventHandlers = new Set<EventHandler>();
  private chatHandlers = new Set<ChatHandler>();
  private connectionHandlers = new Set<ConnectionHandler>();
  private chatConnectionHandlers = new Set<ConnectionHandler>();
  private chatActive = false;
  private chatBuffer: ChatMessage[] = [];
  private chatSeen = new Set<string>();
  private eventSeen = new Set<string>();
  private accountId: string | null | undefined;
  private active = false;
  private reconnectTimers: ReturnType<typeof setTimeout>[] = [];

  start(accountId?: string | null): void {
    if (accountId !== undefined && accountId !== this.accountId) {
      this.accountId = accountId;
      this.clearReplayHistory();
    }
    if (!this.eventSse) this.connectEvents();
    if (!this.chatSse) this.connectChat();
  }

  stop(): void {
    this.eventSse?.close();
    this.chatSse?.close();
    this.eventSse = null;
    this.chatSse = null;
    this.reconnectTimers.forEach(clearTimeout);
    this.reconnectTimers = [];
    this.setActive(false);
    this.setChatActive(false);
    this.clearReplayHistory();
    this.accountId = undefined;
  }

  private clearReplayHistory(): void {
    this.eventSeen.clear();
    this.chatSeen.clear();
    this.chatBuffer = [];
  }

  private port(): string {
    return String(localApiPort());
  }

  private connectEvents(): void {
    const url = `http://localhost:${this.port()}/api/integrations/events/stream`;
    const sse = new EventSource(url);
    this.eventSse = sse;
    sse.addEventListener("event", (msg: MessageEvent) => {
      if (this.eventSse !== sse) return;
      try {
        const e = JSON.parse(msg.data) as StreamEvent;
        if (typeof e.eventId === "string" && e.eventId.length > 0) {
          if (this.eventSeen.has(e.eventId)) return;
          this.eventSeen.add(e.eventId);
          if (this.eventSeen.size > EVENT_SEEN_LIMIT) {
            const oldest = this.eventSeen.values().next().value;
            if (oldest !== undefined) this.eventSeen.delete(oldest);
          }
        }
        this.eventHandlers.forEach((h) => {
          try {
            h(e);
          } catch (err) {
            console.error("[eventBus] event handler failed", err);
          }
        });
      } catch (err) {
        console.error("[eventBus] event parse error", err);
      }
    });
    sse.onopen = () => {
      if (this.eventSse === sse) this.setActive(true);
    };
    sse.onerror = () => {
      if (this.eventSse !== sse) return;
      sse.close();
      if (this.eventSse === sse) this.eventSse = null;
      this.setActive(false);
      const t = setTimeout(() => {
        if (!this.eventSse) this.connectEvents();
      }, RECONNECT_DELAY_MS);
      this.reconnectTimers.push(t);
    };
  }

  private connectChat(): void {
    const url = `http://localhost:${this.port()}/api/integrations/chat/stream`;
    const sse = new EventSource(url);
    this.chatSse = sse;
    sse.onopen = () => {
      if (this.chatSse === sse) this.setChatActive(true);
    };
    sse.addEventListener("chat", (msg: MessageEvent) => {
      if (this.chatSse !== sse) return;
      try {
        const c = JSON.parse(msg.data) as ChatMessage;
        const key = `${c.platform}:${c.id}`;
        if (this.chatSeen.has(key)) return;
        this.chatSeen.add(key);
        this.chatBuffer.push(c);
        if (this.chatBuffer.length > CHAT_BUFFER_LIMIT) {
          const dropped = this.chatBuffer.shift();
          if (dropped) this.chatSeen.delete(`${dropped.platform}:${dropped.id}`);
        }
        this.chatHandlers.forEach((h) => {
          try {
            h(c);
          } catch (err) {
            console.error("[eventBus] chat handler failed", err);
          }
        });
      } catch (err) {
        console.error("[eventBus] chat parse error", err);
      }
    });
    sse.onerror = () => {
      if (this.chatSse !== sse) return;
      sse.close();
      if (this.chatSse === sse) this.chatSse = null;
      this.setChatActive(false);
      const t = setTimeout(() => {
        if (!this.chatSse) this.connectChat();
      }, RECONNECT_DELAY_MS);
      this.reconnectTimers.push(t);
    };
  }

  private setActive(v: boolean): void {
    if (this.active === v) return;
    this.active = v;
    this.connectionHandlers.forEach((h) => {
      try {
        h(v);
      } catch (err) {
        console.error("[eventBus] connection handler failed", err);
      }
    });
  }

  onEvent(h: EventHandler): () => void {
    this.eventHandlers.add(h);
    return () => {
      this.eventHandlers.delete(h);
    };
  }

  private setChatActive(value: boolean): void {
    if (this.chatActive === value) return;
    this.chatActive = value;
    this.chatConnectionHandlers.forEach((handler) => {
      try {
        handler(value);
      } catch (err) {
        console.error("[eventBus] chat connection handler failed", err);
      }
    });
  }

  onChatConnection = (handler: ConnectionHandler): (() => void) => {
    this.chatConnectionHandlers.add(handler);
    return () => { this.chatConnectionHandlers.delete(handler); };
  };

  isChatActive = (): boolean => this.chatActive;

  onChat(h: ChatHandler): () => void {
    this.chatHandlers.add(h);
    return () => {
      this.chatHandlers.delete(h);
    };
  }

  onConnection(h: ConnectionHandler): () => void {
    this.connectionHandlers.add(h);
    return () => {
      this.connectionHandlers.delete(h);
    };
  }

  getChatBuffer(): ChatMessage[] {
    return [...this.chatBuffer];
  }

  isActive(): boolean {
    return this.active;
  }
}

export const eventBusService = new EventBusService();
export type { EventBusService };
